import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { IMotionSchema } from '@/shared/api-client/models/motion/motion-schema';
import { HttpClientHelper, QueryStringBuilder } from '@/shared/api-client/helpers';
import { INewMotion } from '@/shared/api-client/models/motion/new-motion';
import { IPagedList, IPagination } from '@/shared/api-client/models';
import { IMotion } from '@/shared/api-client/models/motion/motion';
import { IEditableMotion } from '@/shared/api-client/models/motion/editable-motion';
import { IEditableMotionTexts } from '@/shared/api-client/models/motion/editable-motion-texts';
import { ArgumentValidator } from '@/shared/helpers';
import { IMotionListEntry } from '@/shared/api-client/models/motion/motion-list-entry';
import { IMultiAssignCurrentDecisionLevelResult } from '@/shared/api-client/models/motion/multi-assign-current-decision-level-result';
import { IMultiAssignDecisionLevelResult } from '@/shared/api-client/models/motion/multi-assign-decision-level-result';
import { IOrganizationalPathLevel } from '../models/organizational-path-level';
import { IMyMotionsListEntry } from '../models/motion/my-motions-list-entry';
import { ListMotionSortField } from '../models/motion/list-motion-sort-field';
import { ISorting } from '../models/sorting';
import { IAggregationProperty } from '../models/aggregations/aggregation-property';
import { StringComparer } from '@/shared/helpers/string-comparer';
import { IMotionReplyRequest } from '../models/motion/motion-process-request';


export interface IMotionFilters {
    motionCategoryIds?: string[];
    currentDecisionLevelPaths?: string[];
    originDecisionLevelPaths?: string[];
    currentDecisionLevelAssignedUsers?: string[];
}

export class MotionClient {

    constructor(private axiosClient: AxiosInstance) { }

    public get apiUrl() {
        return `/motion`;
    }

    public async schema(activityId: string, cultureAlias: string): Promise<IMotionSchema> {

        try {

            const url = new QueryStringBuilder()
                .withCultureAliasIfNotEmpty(cultureAlias)
                .build(`${this.apiUrl}/${activityId}/schema`);

            const response = await this.axiosClient.get(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async create(activityId: string, newMotion: INewMotion, motioningAccessTicketId?: string) {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(newMotion, `newMotion`);

        const url = new QueryStringBuilder()
            .withQueryIfNotEmpty('motioningAccessTicketId', motioningAccessTicketId)
            .build(`${this.apiUrl}/${activityId}`);

        try {
            await this.axiosClient.post(
                url,
                newMotion,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async update(activityId: string, motionId: string, editableMotion: IEditableMotion): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(motionId, `motionId`);
        ArgumentValidator.notNullOrEmpty(editableMotion, `editableMotion`);

        try {
            const response = await this.axiosClient.put(
                `${this.apiUrl}/${activityId}/${motionId}`,
                editableMotion,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async updateTexts(activityId: string, motionId: string, editableMotionTexts: IEditableMotionTexts): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(motionId, `motionId`);
        ArgumentValidator.notNullOrEmpty(editableMotionTexts, `editableMotionTexts`);

        try {
            const response = await this.axiosClient.put(
                `${this.apiUrl}/${activityId}/${motionId}/texts`,
                editableMotionTexts,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async delete(activityId: string, motionId: string): Promise<void> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(motionId, `motionId`);

        const response = await this.axiosClient.delete(`${this.apiUrl}/${activityId}/${motionId}`);

        return response.data;
    }

    public async list(activityId: string, paging?: IPagination, sorting?: ISorting<ListMotionSortField>, filters?: IMotionFilters): Promise<IPagedList<IMotionListEntry>> {

        try {
            const url = new QueryStringBuilder()
                .withPaginationIfNotEmpty(paging)
                .build(`${this.apiUrl}/${activityId}/list`);

            const body = {
                sortField: sorting?.field,
                sortDirection: sorting?.direction,
                motionCategoryIds: filters?.motionCategoryIds ?? [],
                currentDecisionLevelPaths: filters?.currentDecisionLevelPaths ?? [],
                originDecisionLevelPaths: filters?.originDecisionLevelPaths ?? [],
                currentDecisionLevelResponsibility: filters?.currentDecisionLevelAssignedUsers ?? []
            };

            const response = await this.axiosClient.post(
                url,
                body,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async aggregations(activityId: string): Promise<IAggregationProperty[]> {

        try {
            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/${activityId}/aggregations`);

            const response = await this.axiosClient.get(
                url,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async export(activityId: string, sorting?: ISorting<ListMotionSortField>, config?: AxiosRequestConfig, filters?: IMotionFilters, fileName = '', format = '', scope = 'All', motionIds?: string[], useCurrentDecisionLevelDraft = false, templateFile?: { name: string; contentBase64: string } | null): Promise<void> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);

        const body = {
            sortField: sorting?.field,
            sortDirection: sorting?.direction,
            motionCategoryIds: filters?.motionCategoryIds ?? [],
            currentDecisionLevelPaths: filters?.currentDecisionLevelPaths ?? [],
            originDecisionLevelPaths: filters?.originDecisionLevelPaths ?? [],
            currentDecisionLevelResponsibility: filters?.currentDecisionLevelAssignedUsers ?? [],
            scope,
            motionIds: motionIds ?? [],
            format,
            useCurrentDecisionLevelDraft,
            templateFileName: templateFile?.name ?? null,
            templateFileContent: templateFile?.contentBase64 ?? null
        };

        const url = `${this.apiUrl}/${activityId}/export`;

        const response = await this.axiosClient.request<Blob>({
            url,
            method: config?.method ?? "POST",
            data: body,
            responseType: "blob",
            ...config,
        });

        if (StringComparer.isNullOrEmpty(fileName)) {
            fileName = HttpClientHelper.getFilenameFromContentDisposition(response.headers["content-disposition"]) ?? "download"
        }

        HttpClientHelper.triggerFileDownload(
            new Blob([response.data], {
                type: response.data.type || "application/octet-stream",
            }), 
            fileName
        );
    }

    public async downloadDefaultTemplate(fileName = ''): Promise<void> {

        const url = `${this.apiUrl}/export/template`;

        const response = await this.axiosClient.request<Blob>({
            url,
            method: "GET",
            responseType: "blob",
        });

        if (StringComparer.isNullOrEmpty(fileName)) {
            fileName = HttpClientHelper.getFilenameFromContentDisposition(response.headers["content-disposition"]) ?? "motion-export-mall.docx"
        }

        HttpClientHelper.triggerFileDownload(
            new Blob([response.data], {
                type: response.data.type || "application/octet-stream",
            }),
            fileName
        );
    }

    public async listMyMotions(pagination?: IPagination): Promise<IPagedList<IMyMotionsListEntry>> {

        try {

            const url = new QueryStringBuilder()
                .withPaginationIfNotEmpty(pagination)
                .build(`${this.apiUrl}`);

            const response = await this.axiosClient.get(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async resubmit(activityId: string, motionId: string): Promise<void> {

        try {

            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/${activityId}/${motionId}/resubmit`);

            const response = await this.axiosClient.post(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async get(id: string): Promise<IMotion> {
        try {
            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/${id}`);

            const response = await this.axiosClient.get(
                url,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async assignCurrentDecisionLevel(id: string, userId: string): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(userId, `userId`);

        try {

            const response = await this.axiosClient.post(
                `${this.apiUrl}/${id}/assignCurrentDecisionLevel`,
                {
                    userId: userId
                },
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async assignDecisionLevel(id: string, userId: string, level: number): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(userId, `userId`);
        ArgumentValidator.notNullOrEmpty(level, `level`);

        try {

            const response = await this.axiosClient.post(
                `${this.apiUrl}/${id}/assignDecisionLevel/${level}`,
                {
                    userId: userId
                },
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async multiAssignCurrentDecisionLevel(motionIds: string[], userId: string): Promise<IMultiAssignCurrentDecisionLevelResult> {

        ArgumentValidator.notNullOrEmpty(motionIds, `motionIds`);
        ArgumentValidator.notNullOrEmpty(userId, `userId`);

        try {

            const response = await this.axiosClient.post(
                `${this.apiUrl}/multiAssignCurrentDecisionLevel`,
                {
                    motionIds: motionIds,
                    userId: userId
                },
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async multiAssignDecisionLevel(motionIds: string[], userId: string, level: number): Promise<IMultiAssignDecisionLevelResult> {

        ArgumentValidator.notNullOrEmpty(motionIds, `motionIds`);
        ArgumentValidator.notNullOrEmpty(userId, `userId`);
        ArgumentValidator.notNullOrEmpty(level, `level`);

        try {

            const response = await this.axiosClient.post(
                `${this.apiUrl}/multiAssignDecisionLevel/${level}`,
                {
                    motionIds: motionIds,
                    userId: userId
                },
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async reply(id: string, request: IMotionReplyRequest): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(request, `request`);

        try {

            const response = await this.axiosClient.post(
                `${this.apiUrl}/${id}/reply`,
                request,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async replyDraft(id: string, request: IMotionReplyRequest): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(request, `request`);

        try {

            const response = await this.axiosClient.post(
                `${this.apiUrl}/${id}/reply/draft`,
                request,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async listDecisionLevels(id: string): Promise<IOrganizationalPathLevel[]> {

        try {
            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/${id}/decisionLevels`);

            const response = await this.axiosClient.get(
                url,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

}
